import { useEpoch } from "../api/hooks";
import { Card, Empty, ErrorNote, Pill, Progress, Row, Skeleton, Stat } from "../components/ui";
import { compact, daysUntil, gxr, shortDate } from "../lib/format";

export function EpochsScreen() {
  const q = useEpoch();
  if (q.isPending) return <Skeleton className="h-64" />;
  if (q.isError) return <ErrorNote>Could not load epoch data.</ErrorNote>;
  const d = q.data;
  if (!d.season) return <Empty>Season not configured.</Empty>;

  const now = d.now ? new Date(d.now).getTime() : Date.now();
  const cur = d.current;
  const timeProgress = cur
    ? 1 -
      cur.msUntilScheduledEnd /
        (new Date(cur.scheduledEndAt).getTime() - new Date(cur.startsAt).getTime())
    : 0;

  return (
    <div className="space-y-3">
      {d.state === "not_started" && d.startsAt && (
        <Card>
          <Empty>
            Mining starts {shortDate(d.startsAt)} — in {daysUntil(d.startsAt, now)} days.
          </Empty>
        </Card>
      )}

      {cur && (
        <Card
          title={`Epoch ${cur.id} of 6`}
          action={cur.closedEarly ? <Pill tone="bad">closing</Pill> : <Pill tone="good">live</Pill>}
        >
          <div className="grid grid-cols-2 gap-3">
            <Stat
              label="Base reward"
              value={`${gxr(cur.baseReward, 4)} GXR`}
              sub="per claim, before bonuses"
            />
            <Stat
              label="Ends"
              value={`${daysUntil(cur.endsAt, now)} d`}
              sub={shortDate(cur.endsAt)}
            />
          </div>
          <div className="mt-4">
            <Row
              label="Mined this epoch"
              value={`${compact(cur.minedThisEpoch)} / ${compact(cur.emissionCap)}`}
            />
            <Progress value={cur.capProgress} />
            <p className="mt-1 text-xs text-hint">
              {Math.round(cur.capProgress * 100)}% of cap · the epoch ends early if the cap is
              reached first
            </p>
          </div>
          <div className="mt-3">
            <Row
              label="Time elapsed"
              value={`${Math.round(Math.max(0, Math.min(1, timeProgress)) * 100)}%`}
            />
            <Progress value={timeProgress} />
          </div>
        </Card>
      )}

      <Card title="Season">
        <div className="grid grid-cols-3 gap-2">
          <Stat label="Days left" value={d.daysUntilSeasonEnd ?? "—"} />
          <Stat label="Total mined" value={compact(d.stats?.totalMined ?? 0)} />
          <Stat label="Miners" value={compact(d.stats?.totalUsers ?? 0)} />
        </div>
        <p className="mt-3 text-xs text-hint">
          Season ends {shortDate(d.season.endsAt)}. Token generation targeted for{" "}
          {shortDate(d.season.tgeTargetAt)}, when balances become claimable on TON.
        </p>
      </Card>

      <Card title="Timeline">
        <ul className="space-y-2">
          {d.season.epochs.map((e) => {
            const isCurrent = cur?.id === e.id;
            const isPast = new Date(e.endsAt).getTime() <= now;
            return (
              <li
                key={e.id}
                className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
                  isCurrent ? "bg-brand/15" : isPast ? "bg-bg-2 text-hint" : "bg-bg-2"
                }`}
              >
                <div>
                  <span className={`font-semibold ${isCurrent ? "text-brand" : ""}`}>
                    Epoch {e.id}
                  </span>
                  <span className="ml-2 text-xs text-hint">
                    {shortDate(e.startsAt)} – {shortDate(e.endsAt)}
                    {e.closedEarly ? " · closed early" : ""}
                  </span>
                </div>
                <div className="text-right tabular-nums">
                  <div>{gxr(e.baseReward, 4)} GXR</div>
                  <div className="text-xs text-hint">cap {compact(e.emissionCap)}</div>
                </div>
              </li>
            );
          })}
        </ul>
        <p className="mt-3 text-xs text-hint">
          The base reward halves each epoch. Epoch 1 is scheduled for 90 days, epochs 2–6 for 60
          days each, and every epoch is capped at 70M GXR; whichever comes first ends it. 420M GXR
          (42% of supply) can be mined in total.
        </p>
      </Card>
    </div>
  );
}
